/**
 * InterconnectionCard -- wraps the Sankey diagram with context:
 *   - Title and subtitle with key stats
 *   - The SVG Sankey visualization
 *   - Neighbor breakdown table (fuel, DALYs, USD)
 *   - Collapsible methodology notes
 *
 * Renders only when interconnection_breakdown has neighbors.
 */

import { useState } from 'react';
import { FiChevronDown, FiChevronRight, FiGlobe } from 'react-icons/fi';
import { InterconnectionSankey } from './InterconnectionSankey';
import type { InterconnectionBreakdown } from './InterconnectionSankey';
import { toSigFigs } from '../../../utils/format';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InterconnectionCardProps {
  breakdown: InterconnectionBreakdown;
  totalGenerationGwh: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtGwh(v: number): string {
  if (Math.abs(v) >= 100) return v.toFixed(0);
  if (Math.abs(v) >= 10) return v.toFixed(1);
  if (Math.abs(v) >= 1) return v.toFixed(2);
  if (Math.abs(v) >= 0.01) return v.toFixed(3);
  return v.toExponential(1);
}

function fmtUsd(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

const FUEL_COLORS: Record<string, string> = {
  COL: '#6B7280', Coal: '#6B7280', coal: '#6B7280', Lignite: '#6B7280', lignite: '#6B7280',
  Hard_Coal: '#6B7280', hard_coal: '#6B7280', Brown_Coal: '#6B7280', brown_coal: '#6B7280',
  NG: '#F97316', 'Natural Gas': '#F97316', natural_gas: '#F97316',
  Gas: '#F97316', gas: '#F97316', Fossil_Gas: '#F97316', fossil_gas: '#F97316',
  NUC: '#A855F7', Nuclear: '#A855F7', nuclear: '#A855F7',
  OIL: '#92400E', Oil: '#92400E', oil: '#92400E', Petroleum: '#92400E',
  Fossil_Oil: '#92400E', fossil_oil: '#92400E',
  WAT: '#3B82F6', Hydro: '#3B82F6', hydro: '#3B82F6',
  SUN: '#FBBF24', Solar: '#FBBF24', solar: '#FBBF24',
  WND: '#14B8A6', Wind: '#14B8A6', wind: '#14B8A6',
  OTH: '#D1D5DB', Other: '#D1D5DB', other: '#D1D5DB',
  GEO: '#14B8A6', BIO: '#84CC16',
};

function getFuelColor(fuel: string): string {
  if (FUEL_COLORS[fuel]) return FUEL_COLORS[fuel];
  const key = Object.keys(FUEL_COLORS).find(
    (k) => fuel.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? FUEL_COLORS[key] : '#9CA3AF';
}

function getFuelDisplayName(fuel: string): string {
  const map: Record<string, string> = {
    COL: 'Coal', NG: 'Natural Gas', NUC: 'Nuclear', OIL: 'Oil',
    WAT: 'Hydro', SUN: 'Solar', WND: 'Wind', OTH: 'Other',
    GEO: 'Geothermal', BIO: 'Biomass',
    coal: 'Coal', natural_gas: 'Natural Gas', nuclear: 'Nuclear',
    oil: 'Oil', hydro: 'Hydro', solar: 'Solar', wind: 'Wind',
    hard_coal: 'Hard Coal', brown_coal: 'Brown Coal', lignite: 'Lignite',
    fossil_gas: 'Fossil Gas', fossil_oil: 'Fossil Oil', gas: 'Gas',
    Gas: 'Gas', Coal: 'Coal', Oil: 'Oil', Petroleum: 'Petroleum',
    Hard_Coal: 'Hard Coal', Lignite: 'Lignite', Brown_Coal: 'Brown Coal',
    Fossil_Gas: 'Fossil Gas', Fossil_Oil: 'Fossil Oil',
    'Natural Gas': 'Natural Gas', Nuclear: 'Nuclear', Hydro: 'Hydro',
    Solar: 'Solar', Wind: 'Wind', Other: 'Other',
  };
  return map[fuel] ?? fuel.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InterconnectionCard({ breakdown, totalGenerationGwh }: InterconnectionCardProps) {
  const [showNotes, setShowNotes] = useState(false);

  const {
    neighbors,
    total_displaced_via_interconnector_gwh,
    total_endpoint_dalys,
    total_endpoint_usd,
    methodology,
    notes,
  } = breakdown;

  if (!neighbors || neighbors.length === 0) return null;

  // Filter out zones with zero displacement and sort by displaced GWh
  const totalDisplaced = (n: typeof neighbors[0]) =>
    Object.values(n.displaced_generation).reduce((s, v) => s + Math.abs(v), 0);
  const sorted = [...neighbors]
    .filter((n) => totalDisplaced(n) > 0.001)
    .sort((a, b) => totalDisplaced(b) - totalDisplaced(a));

  return (
    <div className="mt-4 rounded-lg bg-surface-700 border border-border p-5">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FiGlobe className="text-quantum-400" size={18} />
            <h3 className="text-body-sm font-semibold text-text-primary">
              Cross-Border Impact
            </h3>
          </div>
          <p className="text-xs text-text-muted">
            {fmtGwh(total_displaced_via_interconnector_gwh)} GWh displaced via interconnection across{' '}
            {neighbors.length} neighboring zone{neighbors.length !== 1 ? 's' : ''}
          </p>
        </div>
        {/* Summary pills */}
        <div className="flex gap-3">
          <div className="text-right">
            <div className="text-[10px] text-text-muted uppercase tracking-wider">Health</div>
            <div className="text-sm font-semibold text-health-400">
              {toSigFigs(Math.abs(total_endpoint_dalys), 3)} DALYs
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-text-muted uppercase tracking-wider">Economic</div>
            <div className="text-sm font-semibold text-economic-400">
              {fmtUsd(Math.abs(total_endpoint_usd))}
            </div>
          </div>
        </div>
      </div>

      {/* Sankey visualization */}
      <InterconnectionSankey breakdown={breakdown} totalGenerationGwh={totalGenerationGwh} />

      {/* Neighbor table */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border/50">
              <th className="text-left py-2 px-2 text-text-muted font-medium">Zone</th>
              <th className="text-left py-2 px-2 text-text-muted font-medium">Country</th>
              <th className="text-right py-2 px-2 text-text-muted font-medium">Displaced (GWh)</th>
              <th className="text-left py-2 px-2 text-text-muted font-medium">Fuel Mix</th>
              <th className="text-right py-2 px-2 text-text-muted font-medium">DALYs</th>
              <th className="text-right py-2 px-2 text-text-muted font-medium">USD</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((n) => {
              const fuels = Object.entries(n.displaced_generation)
                .filter(([, v]) => Math.abs(v) > 0)
                .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a));

              return (
                <tr
                  key={n.neighbor_area_code}
                  className="border-b border-border/30 hover:bg-surface-600/40 transition-colors"
                >
                  <td className="py-2 px-2 text-text-primary font-medium">
                    {n.neighbor_name || n.neighbor_area_code}
                  </td>
                  <td className="py-2 px-2 text-text-secondary">
                    {n.eco_geography}
                  </td>
                  <td className="py-2 px-2 text-right text-text-primary tabular-nums">
                    {fmtGwh(Object.values(n.displaced_generation).reduce((s, v) => s + Math.abs(v), 0))}
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex flex-wrap gap-1.5">
                      {fuels.map(([fuel, gwh]) => (
                        <span
                          key={fuel}
                          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
                          style={{
                            backgroundColor: `${getFuelColor(fuel)}20`,
                            color: getFuelColor(fuel),
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full inline-block"
                            style={{ backgroundColor: getFuelColor(fuel) }}
                          />
                          {getFuelDisplayName(fuel)} {fmtGwh(Math.abs(gwh))}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right text-health-400 tabular-nums">
                    {toSigFigs(Math.abs(n.endpoint_dalys), 3)}
                  </td>
                  <td className="py-2 px-2 text-right text-economic-400 tabular-nums">
                    {fmtUsd(Math.abs(n.endpoint_usd))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Methodology notes -- collapsible */}
      {(methodology || (notes && notes.length > 0)) && (
        <div className="mt-4 pt-3 border-t border-border/40">
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors"
          >
            {showNotes ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
            Methodology
          </button>
          {showNotes && (
            <div className="mt-2 text-xs text-text-muted space-y-1.5 pl-4">
              {methodology && <p>{methodology}</p>}
              {notes && notes.length > 0 && (
                <ul className="list-disc pl-4 space-y-0.5">
                  {notes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
